/**
 * Test-environment maintenance contract.
 *
 * One operation: clear the call history so a QA tester can run the same
 * scenario again on a clean console. A call cannot be un-made — once it has an
 * outcome and an analysis it lives on the dashboard and the interactions page —
 * which is correct in production and makes a second test run hard to read.
 *
 * Nothing here is available where `MAINTENANCE_RESET_ENABLED` is not explicitly
 * on, which is the only reason it is safe to expose from the browser at all.
 */
import { z } from 'zod';

/**
 * What a reset did, in the same terms the UI reports it.
 *
 * Counted rather than acknowledged with a bare 200: a tester who presses this
 * needs to know it actually found something, and "0 calls" is the difference
 * between "already clean" and "pointing at the wrong database".
 */
export const historyResetResultSchema = z.object({
  /** Resolved calls deleted, with their events and transcripts. */
  calls: z.number().int().nonnegative(),
  interactions: z.number().int().nonnegative(),
  /** Call alerts deleted, and everyone's read marks for them. */
  alerts: z.number().int().nonnegative(),
  /**
   * Calls still in progress, which were deliberately left alone. Their end
   * event is still to come, and deleting the row would leave it matching
   * nothing.
   */
  callsInFlight: z.number().int().nonnegative(),
});
export type HistoryResetResult = z.infer<typeof historyResetResultSchema>;

/** True when nothing was there to clear — a distinct outcome, not a failure. */
export function resetWasNoOp(result: HistoryResetResult): boolean {
  return result.calls === 0 && result.interactions === 0 && result.alerts === 0;
}

/**
 * A one-line summary of a reset, for the button to report in place.
 *
 * In the contract rather than the component so the script and the UI describe
 * the same operation the same way.
 */
export function describeReset(result: HistoryResetResult): string {
  const plural = (n: number, one: string, many = `${one}s`): string =>
    `${String(n)} ${n === 1 ? one : many}`;

  const inFlight =
    result.callsInFlight > 0
      ? ` ${plural(result.callsInFlight, 'call')} still in progress, left alone.`
      : '';

  if (resetWasNoOp(result)) return `Already clean — nothing to clear.${inFlight}`;

  const parts: string[] = [];
  if (result.calls > 0) parts.push(plural(result.calls, 'call'));
  if (result.interactions > 0) parts.push(plural(result.interactions, 'interaction'));
  if (result.alerts > 0) parts.push(plural(result.alerts, 'alert'));

  return `Cleared ${parts.join(', ')}.${inFlight}`;
}
